import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import EntityPicker from "./EntityPicker";
import OperativePicker from "./OperativePicker";

const EMPTY = { type: "", title: "", location: "", description: "", isolation_details: "", valid_from: "", valid_to: "", issued_to: [], precautions: [], rams_id: "", entity_type: "", entity_id: "", entity_title: "" };
const toLocal = (iso) => (iso ? iso.slice(0, 16) : "");

/** Create / edit a permit to work. Precautions are prefilled from the library for the chosen permit type. */
export default function PermitDialog({ open, onOpenChange, permit, permitTypes, onSaved }) {
  const { user } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [rams, setRams] = useState([]);
  const [busy, setBusy] = useState(false);
  const editing = !!permit?.id;
  const locked = editing && permit.status !== "draft";

  useEffect(() => {
    if (!open) return;
    setF(permit ? { ...EMPTY, ...permit, valid_from: toLocal(permit.valid_from), valid_to: toLocal(permit.valid_to), precautions: (permit.precautions || []).map(p => p.text) } : EMPTY);
  }, [open, permit]);

  useEffect(() => {
    if (!open) return;
    const params = f.entity_type && f.entity_id ? { entity_type: f.entity_type, entity_id: f.entity_id } : { status: "issued" };
    API.get("/hs/rams", { params }).then(({ data }) => setRams(data.rams || [])).catch(() => setRams([]));
  }, [open, f.entity_type, f.entity_id]);

  const set = (patch) => setF(x => ({ ...x, ...patch }));
  const pickType = (type) => {
    const t = permitTypes.find(p => p.key === type);
    set({ type, title: f.title || (t ? t.label : ""), precautions: editing && f.precautions.length ? f.precautions : (t?.precautions || []) });
  };
  const setPrec = (i, v) => set({ precautions: f.precautions.map((p, idx) => (idx === i ? v : p)) });

  const save = async () => {
    if (!f.type) return toast.error("Choose a permit type");
    setBusy(true);
    try {
      const body = { ...f, precautions: f.precautions.filter(Boolean).map(text => ({ text, checked: (permit?.precautions || []).find(p => p.text === text)?.checked || false })) };
      const { data } = editing ? await API.put(`/hs/permits/${permit.id}`, body) : await API.post("/hs/permits", body);
      toast.success(editing ? "Permit updated" : `${data.reference} created`);
      onSaved(data); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="permit-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">{editing ? `Edit ${permit.reference}` : "New permit to work"}</DialogTitle></DialogHeader>
        {locked && <p className="text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded p-2">This permit is {permit.status} — only the notes and precaution wording can be changed.</p>}
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px]">Permit type</Label>
            <select value={f.type} onChange={e => pickType(e.target.value)} disabled={locked} className="mt-1 h-9 w-full rounded-md border border-border bg-background text-sm px-2" data-testid="permit-type">
              <option value="">Choose…</option>
              {permitTypes.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              <option value="other">Other</option>
            </select>
          </div>
          <div><Label className="text-[11px]">Title</Label><Input value={f.title} onChange={e => set({ title: e.target.value })} className="mt-1 h-9 text-sm" data-testid="permit-title" /></div>
          <div className="md:col-span-2"><Label className="text-[11px]">Location / area of work</Label><Input value={f.location} onChange={e => set({ location: e.target.value })} placeholder="e.g. Plant room B2, riser 3" className="mt-1 h-9 text-sm" data-testid="permit-location" /></div>
          <div><Label className="text-[11px]">Valid from</Label><Input type="datetime-local" value={f.valid_from} onChange={e => set({ valid_from: e.target.value })} disabled={locked} className="mt-1 h-9 text-sm" data-testid="permit-valid-from" /></div>
          <div><Label className="text-[11px]">Valid to</Label><Input type="datetime-local" value={f.valid_to} onChange={e => set({ valid_to: e.target.value })} disabled={locked} className="mt-1 h-9 text-sm" data-testid="permit-valid-to" /></div>
        </div>

        <div>
          <Label className="text-[11px]">Issued to</Label>
          <div className="mt-1"><OperativePicker value={f.issued_to} onChange={v => set({ issued_to: v })} testPrefix="permit-issued-to" /></div>
        </div>

        <details className="border border-border rounded-md" open={!f.entity_id && !editing ? undefined : false}>
          <summary className="cursor-pointer text-xs px-3 py-2 font-medium" data-testid="permit-link-job-toggle">
            Linked job: {f.entity_id ? <span className="text-primary">{f.entity_title || f.entity_id}</span> : <span className="text-muted-foreground">none — click to link</span>}
          </summary>
          <div className="p-3 border-t border-border">
            <EntityPicker compact onPick={(p) => set({ entity_type: p.entity_type, entity_id: p.entity_id, entity_title: p.title, rams_id: "" })} />
            {f.entity_id && <button type="button" onClick={() => set({ entity_type: "", entity_id: "", entity_title: "" })} className="text-[11px] text-red-600 hover:underline mt-2">Unlink job</button>}
          </div>
        </details>

        <div>
          <Label className="text-[11px]">Associated RAMS</Label>
          <select value={f.rams_id} onChange={e => set({ rams_id: e.target.value })} className="mt-1 h-9 w-full rounded-md border border-border bg-background text-sm px-2" data-testid="permit-rams">
            <option value="">None</option>
            {rams.map(r => <option key={r.id} value={r.id}>{r.reference} v{r.version} — {r.title} ({r.status})</option>)}
          </select>
        </div>

        <div>
          <div className="flex items-center justify-between"><Label className="text-[11px]">Precautions (operative confirms each on site)</Label>
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => set({ precautions: [...f.precautions, ""] })} data-testid="permit-precaution-add"><Plus size={12} className="mr-1" /> Add</Button></div>
          <div className="space-y-1.5 mt-1">
            {f.precautions.map((p, i) => (
              <div key={i} className="flex gap-1.5">
                <Input value={p} onChange={e => setPrec(i, e.target.value)} className="h-8 text-xs" data-testid={`permit-precaution-${i}`} />
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600" onClick={() => set({ precautions: f.precautions.filter((_, idx) => idx !== i) })}><Trash size={14} /></Button>
              </div>
            ))}
            {f.precautions.length === 0 && <p className="text-xs text-muted-foreground">Pick a permit type to load standard precautions, or add your own.</p>}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div><Label className="text-[11px]">Description of work</Label><Textarea value={f.description} onChange={e => set({ description: e.target.value })} rows={3} className="mt-1 text-xs" data-testid="permit-description" /></div>
          <div><Label className="text-[11px]">Isolation / lock-off details</Label><Textarea value={f.isolation_details} onChange={e => set({ isolation_details: e.target.value })} rows={3} className="mt-1 text-xs" placeholder="Circuits, valves, lock numbers, who holds the keys" data-testid="permit-isolation" /></div>
        </div>
        <p className="text-[10px] text-muted-foreground">Issued by {permit?.issued_by_name || user?.name}. Issue the permit from the list once the validity window and crew are set.</p>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="permit-save">{busy ? "Saving…" : editing ? "Save changes" : "Create permit"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
